import { AxiosResponse } from "axios"
import defaultHeaders from "../headers"
import client from "./client"
import { DeviceType } from "./oaMembership"

interface Response<T> {
    content: T,
    metadata: {
        status: MetadataStatus
    }
}

export enum MetadataStatus {
    SUCCESS = `SUCCESS`,
}

enum ServiceType {
    SIGN_UP = `SIGN_UP`,
    RENEWAL = `RENEWAL`,
    ARREARS = `ARREARS`,
    VOUCHER = `VOUCHER`,
    CHANGE_MODE = `CHANGE_MODE`,
    RETRY_PAYMENT = `RETRY_PAYMENT`,
}

enum CardType {
    VISA,
    MASTERCARD,
}

enum PaymentMethod {
    CARDPAYMENT,
}

enum OrderStatus {
    NEW = `NEW`,
    PENDING = `PENDING`,
    SUCCESS = `SUCCESS`,
    CANCELLED = `CANCELLED`,
    FAILED = `FAILED`,
    SEMI_SUCCESS_ONE_TIME = `SEMI_SUCCESS_ONE_TIME`,
    SEMI_SUCCESS_RECURRING = `SEMI_SUCCESS_RECURRING`
}

enum PayementStatus {
    PENDING,
}

interface SignupFees {
    amount: number,
    startDate: string,
    endDate: string,
    feeType: number,
    month: number,
}

interface HistoryResponse {
    errorDescription: string,
    orderStatus: string,
    orderCurrency: string,
    orderAmount: string,
    date: string,
    serviceType: string,
    orderInfoId: string,
    noOfMonths: number,
    endDate: string,
    startDate: string,
    membershipType: string,
    membershipId: number,
}

interface OrderRequest {
    feeType?: number,
    endDate: string,
    startDate: string,
    serviceType: ServiceType,
    noOfMonths: number,
    amount: string,
    isOneTimePayment: boolean,
    isRecurringPayment: boolean,
    membershipId: number,
    deviceType: DeviceType,
    renewalCampaignCode?: string,
}

interface OrderResponseWithOrderStatus {
    feeType?: number,
    endDate: string,
    startDate: string,
    serviceType: ServiceType,
    noOfMonths: number,
    amount: string,
    isOneTimePayment: boolean,
    isRecurringPayment: boolean,
    membershipId: number,
    deviceType: DeviceType,
    orderStatus: OrderStatus
    renewalCampaignCode: string,
}

interface CreateOrderResponse {
    modifiedDttm: string,
    createdDttm: string,
    paymentTypeCode: string,
    description: string,
    errorDescription: string,
    errorCode: string,
    recurringBranchCode: string,
    recurringDDARefNo: string,
    recurringAcctNo: string,
    recurringAcctName: string,
    recurringCardType: CardType,
    recurringPaymentMethod: PaymentMethod,
    recurringPaymentStatus: PayementStatus,
    recurringToken: string,
    recurringMerchantId: string,
    oneTimeBranchCode: string,
    oneTimeDDARefNo: string,
    oneTimeAcctNo: string,
    oneTimeAcctName: string,
    oneTimeCardType: CardType,
    oneTimePaymentMethod: PaymentMethod,
    oneTimePaymentStatus: PayementStatus,
    oneTimeCardExpiryDate: string,
    oneTimeMerchantId: string,
    pgOrderId: string,
    orderStatus: OrderStatus,
    serviceType: ServiceType,
    isOneTimePayment: true,
    isRecurringPayment: true,
    orderAmount: string,
    clientOrderId: string,
    id: number,
}

interface FeeResponse {
    feeType: number,
    month: number,
    amount: number,
    startDate: string,
    endDate: string,
    renewalGiftDescription?: string
    renewalGiftImage?: string
    renewalPeriodId?: number,
    renewalCampaignCode?: string,
}

enum MembershipType {
    OA
}

interface MembershipRenewalFeesResponse {
    pendingOrderId: string,
    paymentStatus: PayementStatus,
    membershipTypeCode: MembershipType,
    membershipId: number,
    arrearFlag: true,
    fees: FeeResponse[],
    message: string
    isSpecialCampaign: boolean,
    isSubtypeMembership: boolean,
    // isShowErrorSpecialCampaign: boolean,
    isBlocked: boolean,
    Reason: string,
}

interface ShowPaymentOptionResponse {
    showPaymentOption: boolean,
}

interface MembershipPayment {
    ucemErrorCode: string,
    ucemReceiptKey: string,
    ucemRetDttm: string,
    ucemStatus: true,
    ucemPaymentGuid: string,
    notificationSent: true,
    feeType: string,
    endDate: string,
    startDate: string,
    noOfMonths: number,
    orderInfoId: string,
    membershipId: string,
}

interface Membership {
    campaignCode: string,
    lastPaymentEndDate: string,
    lastPaymentStartDate: string,
    cardNumber: string,
    nameOnCard: string,
    userId: string,
    ucemGiftGuid: string,
    ucemUpdateStatus: string,
    ucemCreateStatus: string,
    ucemMembershipGuid: string,
    giftItemName: string,
    createGift: true,
    unionName: string,
    unionCode: string,
    status: string,
    startDate: string,
    relationship: string,
    optInNebo: true,
    modifiedDttm: string,
    modifiedBy: string,
    membershipTypeCode: MembershipType,
    lastPaidDate: string,
    deletedDttm: string,
    deletedBy: string,
    createdDttm: string,
    createdBy: string,
    id: string,
}

interface PaymentResponse {
    payment: CreateOrderResponse,
    membershipPayment: MembershipPayment,
    membership: Membership,
    campaignSUGStatus: boolean,
    renewalCampaignCode: string,
}

interface PaymentInfo {
    amount: number,
    startDate: string,
    endDate: string,
    feeType: number,
    month: number,
    orderStatus: OrderStatus,
    campaignSUGStatus: boolean,
}

const getPaymentHistory = () => {
    return client.get<Response<HistoryResponse[]>>(`payment/history`)
}

const postCreateOrder = (payload: OrderRequest) => {
    return client.post<Response<CreateOrderResponse>>(
        `payment/create-order`,
        payload,
    )
}

const getSignupFee = (membershipId: number) => {
    return client.get<Response<FeeResponse>>(
        `payment/get-signup-fee/${membershipId}`,
    )
}

const getMembershipRenewalFees = (membershipId: string, renewalCampaignCode: string) => {
    return client.get<Response<MembershipRenewalFeesResponse[]>>(
        `payment/membership-renewal-fees/${DeviceType.WEB.toLowerCase()}`,
        {
            params: {
                membershipId: membershipId,
                renewalCampaignCode: renewalCampaignCode || undefined,
            },
            headers: defaultHeaders(),
        },
    )
}

const getShowPaymentOption = () => {
    return client.get<Response<ShowPaymentOptionResponse>>(
        `payment/show-payment-option`,
    )
}

const getPayment = (orderId: string): Promise<AxiosResponse<Response<PaymentResponse>, any>> => {
    return client.get<Response<PaymentResponse>>(`payment/${orderId}/web`)
}

const paymentInfoFromPaymentResponse = (paymentResponse: PaymentResponse): PaymentInfo => {
    // const hasUpfrontPaymentFailed = paymentResponse.payment.orderStatus == OrderStatus.SEMI_SUCCESS_RECURRING

    return {
        // amount: hasUpfrontPaymentFailed ? 0 : parseFloat(paymentResponse.payment.orderAmount),
        amount: parseFloat(paymentResponse.payment.orderAmount),
        startDate: paymentResponse.membershipPayment.startDate,
        endDate: paymentResponse.membershipPayment.endDate,
        feeType: parseInt(paymentResponse.membershipPayment.feeType),
        month: paymentResponse.membershipPayment.noOfMonths,
        orderStatus: paymentResponse.payment.orderStatus,
        campaignSUGStatus: paymentResponse.campaignSUGStatus,
    }
}

const extractPaymentResponseContent = (response: AxiosResponse<Response<PaymentResponse>, any>): PaymentResponse => {
    return response.data.content
}

const extractClientOrderId = (response: AxiosResponse<Response<CreateOrderResponse>, any>): string => {
    return response.data.content.clientOrderId
}

export { getPaymentHistory, postCreateOrder, getSignupFee, getMembershipRenewalFees, getShowPaymentOption, getPayment, paymentInfoFromPaymentResponse, extractPaymentResponseContent, extractClientOrderId, ServiceType, OrderStatus }
export type { PaymentResponse, FeeResponse, OrderRequest,OrderResponseWithOrderStatus, SignupFees, MembershipPayment, Response, PaymentInfo }
